import React, { useState, useEffect, useRef } from 'react';

// FastAPI/Pydantic returns `detail` as an array of validation objects on 422.
// Rendering that array as a React child crashes the toast — coerce to string.
function detailToString(detail) {
    if (detail == null) return '';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        return detail
            .map(d => (d && typeof d === 'object' && d.msg) ? d.msg : JSON.stringify(d))
            .join('; ');
    }
    if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
    return String(detail);
}

const rowStyle = {
    background: '#fff', borderRadius: 14, border: '1px solid #e7e9ee',
    marginBottom: 10, overflow: 'hidden',
};
const identityStyle = {
    display: 'flex', alignItems: 'center', gap: 10, padding: '13px 13px',
};
const sizeStyle = {
    fontSize: 22, fontWeight: 700, color: '#202223', flex: 'none', minWidth: 32,
};
const skuStyle = {
    fontFamily: 'var(--font-mono, ui-monospace, "SF Mono", Menlo, Consolas, monospace)',
    fontSize: 12, color: '#374151',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};
const colorStyle = { fontSize: 12, color: '#9ca3af', marginTop: 1 };
const stockedStyle = {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 12.5, fontWeight: 600, color: '#1a7f4f',
};
const dotStyle = { width: 8, height: 8, borderRadius: '50%', background: '#1a7f4f' };
const stockHereStyle = {
    background: '#1a7f4f', color: '#fff', border: 'none',
    borderRadius: 9, padding: '9px 16px', fontSize: 13, fontWeight: 600,
    cursor: 'pointer',
};
const conflictStyle = {
    background: '#fff4e1', color: '#7a3e00',
    fontSize: 12, padding: '8px 13px', borderTop: '1px solid #f0e1bd',
};
const fieldRowStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 13px', borderTop: '1px solid #f0f1f3', gap: 12,
};
const labelStyle = { fontSize: 13.5, color: '#6d7175' };
const stepBtnStyle = {
    width: 40, height: 40, borderRadius: 10, border: '1px solid #c9cccf',
    background: '#fff', color: '#1a4fa0', fontSize: 22, fontWeight: 700,
    cursor: 'pointer', lineHeight: 1,
};
const stepInputStyle = {
    width: 54, height: 40, borderRadius: 10, border: '1px solid #c9cccf',
    textAlign: 'center', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
    fontSize: 16, outline: 'none',
};
const binInputStyle = {
    width: 120, padding: '6px 10px',
    border: '1px solid var(--wh-bin-border)', background: 'var(--wh-bin-input-bg)',
    borderRadius: 9, color: 'var(--wh-bin-value)',
    fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700,
    fontSize: 18, textAlign: 'center', textTransform: 'uppercase',
    outline: 'none',
};
const barcodeInputStyle = {
    width: 160, padding: '6px 10px',
    border: '1px solid transparent', background: '#f3f4f6',
    borderRadius: 9, color: '#202223',
    fontFamily: 'var(--font-mono, ui-monospace, monospace)',
    fontSize: 13, textAlign: 'right', outline: 'none',
};
const segTrackStyle = {
    display: 'flex', background: '#f3f4f6', borderRadius: 9, padding: 2,
};
const segBtnStyle = {
    border: 'none', borderRadius: 7, padding: '6px 14px',
    fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
};
const segDenyActiveStyle  = { background: '#1a4fa0', color: '#fff' };
const segAllowActiveStyle = { background: '#b45309', color: '#fff' };
const segInactiveStyle    = { background: 'transparent', color: '#6d7175' };

export default function WarehouseVariantRow({
    storeKey, shopifyLocationId, variant, showVariantBin, showVariantOverstock,
    rfidShelvedBoxes = 0, apiFetch, onPatchVariant, onError, onSuccess,
}) {
    const isActive = !!variant.is_active_at_location;
    const [qty, setQty] = useState(variant.on_hand ?? 0);
    const [bin, setBin] = useState(variant.bin ?? '');
    const [overstock, setOverstock] = useState(variant.overstock_bin ?? '');
    const [barcode, setBarcode] = useState(variant.barcode ?? '');
    const [policy, setPolicy] = useState(variant.inventory_policy ?? 'DENY');
    const [busy, setBusy] = useState(null);
    const [conflict, setConflict] = useState(null);
    const [flash, setFlash] = useState(false);
    const flashTimer = useRef(null);

    useEffect(() => { setQty(variant.on_hand ?? 0); }, [variant.on_hand]);
    useEffect(() => { setBin(variant.bin ?? ''); }, [variant.bin]);
    useEffect(() => { setOverstock(variant.overstock_bin ?? ''); }, [variant.overstock_bin]);
    useEffect(() => { setBarcode(variant.barcode ?? ''); }, [variant.barcode]);
    useEffect(() => { setPolicy(variant.inventory_policy ?? 'DENY'); }, [variant.inventory_policy]);
    useEffect(() => () => clearTimeout(flashTimer.current), []);

    const triggerFlash = () => {
        setFlash(true);
        clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setFlash(false), 1200);
    };

    const color = (variant.selected_options || []).find(o =>
        /color|colour/i.test(o.name)
    )?.value || '';
    const sizeLabel = (variant.selected_options || []).find(o =>
        /size/i.test(o.name)
    )?.value || (variant.selected_options?.[0]?.value) || '';

    async function saveQty(next) {
        if (busy || !isActive) return;
        if (next === variant.on_hand) return;
        setBusy('qty');
        try {
            const r = await apiFetch('/api/portal/warehouse/inventory/set', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    label: variant.sku || null,
                    inventory_item_gid: variant.inventory_item_gid,
                    shopify_location_id: shopifyLocationId,
                    qty_before: variant.on_hand ?? 0,
                    qty_after:  next,
                    variant_gid: variant.variant_gid,
                }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) { onError?.(detailToString(data.detail) || `Save failed (${r.status})`); return; }
            if (data.conflict) {
                setConflict({ current_qty: data.current_qty });
                setQty(data.current_qty);
                onPatchVariant?.({ on_hand: data.current_qty });
                return;
            }
            onPatchVariant?.({ on_hand: next });
            onSuccess?.(`On hand set to ${next}`);
            setConflict(null);
            triggerFlash();
        } finally { setBusy(null); }
    }

    async function saveBin() {
        const nextBin = bin.trim().toUpperCase();
        const prevBin = (variant.bin ?? '').toUpperCase();
        if (busy || nextBin === prevBin) return;
        setBusy('bin');
        try {
            const r = await apiFetch('/api/portal/warehouse/metafield/set', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    label: variant.sku || null,
                    owner_gid: variant.variant_gid,
                    field: 'bin',
                    shopify_location_id: shopifyLocationId,
                    value_before: prevBin || null,
                    value_after:  nextBin,
                }),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                onError?.(detailToString(data.detail) || `Bin save failed (${r.status})`);
                return;
            }
            onPatchVariant?.({ bin: bin.trim().toUpperCase() });
            onSuccess?.('Bin saved');
            triggerFlash();
        } finally { setBusy(null); }
    }

    async function saveOverstock() {
        const nextVal = overstock.trim().toUpperCase();
        const prevVal = (variant.overstock_bin ?? '').toUpperCase();
        if (busy || nextVal === prevVal) return;
        setBusy('overstock');
        try {
            const r = await apiFetch('/api/portal/warehouse/metafield/set', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    label: variant.sku || null,
                    owner_gid: variant.variant_gid,
                    field: 'overstock',
                    shopify_location_id: shopifyLocationId,
                    value_before: prevVal || null,
                    value_after:  nextVal,
                }),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                onError?.(detailToString(data.detail) || `Overstock save failed (${r.status})`);
                return;
            }
            onPatchVariant?.({ overstock_bin: nextVal });
            onSuccess?.('Overstock saved');
            triggerFlash();
        } finally { setBusy(null); }
    }

    async function saveBarcode() {
        const nextBc = barcode.trim();
        const prevBc = (variant.barcode ?? '').trim();
        if (busy || nextBc === prevBc) return;
        setBusy('barcode');
        try {
            const r = await apiFetch('/api/portal/warehouse/variant/update', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    label: variant.sku || null,
                    variant_gid: variant.variant_gid,
                    shopify_location_id: shopifyLocationId,
                    barcode_before: prevBc,
                    barcode_after:  nextBc,
                }),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                onError?.(detailToString(data.detail) || `Barcode save failed (${r.status})`);
                return;
            }
            onPatchVariant?.({ barcode: nextBc });
            onSuccess?.('Barcode saved');
            triggerFlash();
        } finally { setBusy(null); }
    }

    async function savePolicy(next) {
        const prev = policy;
        if (busy || next === prev) return;
        setBusy('policy');
        setPolicy(next);
        try {
            const r = await apiFetch('/api/portal/warehouse/variant/update', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    label: variant.sku || null,
                    variant_gid: variant.variant_gid,
                    shopify_location_id: shopifyLocationId,
                    policy_before: prev,
                    policy_after:  next,
                }),
            });
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                onError?.(detailToString(data.detail) || `Overselling save failed (${r.status})`);
                setPolicy(variant.inventory_policy ?? 'DENY');
                return;
            }
            onPatchVariant?.({ inventory_policy: next });
            onSuccess?.(`Overselling: ${next === 'DENY' ? 'Deny' : 'Allow'}`);
            triggerFlash();
        } finally { setBusy(null); }
    }

    async function activate() {
        if (busy || isActive) return;
        setBusy('activate');
        try {
            const r = await apiFetch('/api/portal/warehouse/inventory/activate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    label: variant.sku || null,
                    inventory_item_gid: variant.inventory_item_gid,
                    shopify_location_id: shopifyLocationId,
                    variant_gid: variant.variant_gid,
                }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) { onError?.(detailToString(data.detail) || 'Activation failed'); return; }
            onPatchVariant?.({ is_active_at_location: true, on_hand: data.on_hand ?? 0 });
            onSuccess?.('Stocked here');
            triggerFlash();
        } finally { setBusy(null); }
    }

    return (
        <div className={`warehouse-row${flash ? ' warehouse-row--save-flash' : ''}`} style={rowStyle}>
            <div style={identityStyle}>
                <div style={sizeStyle}>{sizeLabel}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={skuStyle}>{variant.sku || '—'}</div>
                    <div style={colorStyle}>{color}</div>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                    {isActive
                        ? <span style={stockedStyle}><span style={dotStyle}/>Stocked</span>
                        : <button type="button" onClick={activate} disabled={busy === 'activate'} style={stockHereStyle}>
                              {busy === 'activate' ? '…' : 'Stock here'}
                          </button>
                    }
                </div>
            </div>

            {conflict && (
                <div style={conflictStyle}>
                    Changed to {conflict.current_qty} on another device — re-enter to overwrite.
                </div>
            )}

            <FieldRow label="On hand">
                <Stepper
                    value={qty}
                    disabled={!isActive || busy === 'qty'}
                    onChange={(n) => { setQty(n); saveQty(n); }}
                />
            </FieldRow>

            {showVariantBin && (
                <FieldRow label="Bin">
                    <input
                        value={bin}
                        onChange={(e) => setBin(e.target.value)}
                        onBlur={saveBin}
                        disabled={busy === 'bin'}
                        style={binInputStyle}
                    />
                </FieldRow>
            )}
            {showVariantOverstock && (
                <FieldRow label="Overstock">
                    {rfidShelvedBoxes > 0 && (
                        <div className="warehouse-overstock-rfid-note" role="note">
                            {rfidShelvedBoxes} tagged box{rfidShelvedBoxes === 1 ? '' : 'es'} shelved here — the next box move rewrites this value. Edit the Overstock page instead.
                        </div>
                    )}
                    <input
                        value={overstock}
                        onChange={(e) => setOverstock(e.target.value)}
                        onBlur={saveOverstock}
                        disabled={busy === 'overstock'}
                        style={binInputStyle}
                    />
                </FieldRow>
            )}

            <FieldRow label="Barcode">
                <input
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    onBlur={saveBarcode}
                    disabled={busy === 'barcode'}
                    style={barcodeInputStyle}
                />
            </FieldRow>

            <FieldRow label="Overselling">
                <Segmented
                    value={policy}
                    onChange={savePolicy}
                    disabled={busy === 'policy'}
                />
            </FieldRow>
        </div>
    );
}

function FieldRow({ label, children }) {
    return (
        <div style={fieldRowStyle}>
            <span style={labelStyle}>{label}</span>
            <div>{children}</div>
        </div>
    );
}

function Stepper({ value, disabled, onChange }) {
    const v = Number(value) || 0;
    const color = v === 0 ? '#d72c0d' : v <= 2 ? '#b45309' : '#202223';
    const [draft, setDraft] = useState(String(v));
    useEffect(() => { setDraft(String(v)); }, [v]);
    const commitDraft = () => {
        const n = Math.max(0, Number(draft) || 0);
        if (n !== v) onChange(n);
        else setDraft(String(v));
    };
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button type="button" disabled={disabled} onClick={() => onChange(Math.max(0, v - 1))} style={stepBtnStyle}>−</button>
            <input
                type="number"
                value={draft}
                disabled={disabled}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commitDraft}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                style={{ ...stepInputStyle, color }}
            />
            <button type="button" disabled={disabled} onClick={() => onChange(v + 1)} style={stepBtnStyle}>+</button>
        </div>
    );
}

function Segmented({ value, onChange, disabled }) {
    return (
        <div style={segTrackStyle}>
            <button type="button" disabled={disabled} onClick={() => onChange('DENY')}
                    style={{ ...segBtnStyle, ...(value === 'DENY' ? segDenyActiveStyle : segInactiveStyle) }}>
                Deny
            </button>
            <button type="button" disabled={disabled} onClick={() => onChange('CONTINUE')}
                    style={{ ...segBtnStyle, ...(value === 'CONTINUE' ? segAllowActiveStyle : segInactiveStyle) }}>
                Allow
            </button>
        </div>
    );
}
